import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { JobFileUpload } from './JobFileUpload';
import { JobFileViewer } from './JobFileViewer';
import { supabase } from '@/integrations/supabase/client';

interface JobDocumentsTabProps {
  jobId: string;
  defaultSubTab?: 'files' | 'upload';
}

export const JobDocumentsTab: React.FC<JobDocumentsTabProps> = ({ jobId, defaultSubTab }) => {
  const [userRole, setUserRole] = useState<'client' | 'vendor'>('client');
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentSubTab, setCurrentSubTab] = useState<'files' | 'upload'>(defaultSubTab || 'files');

  // Sync with defaultSubTab prop when it changes (e.g., from navigation)
  useEffect(() => {
    if (defaultSubTab) {
      setCurrentSubTab(defaultSubTab);
    }
  }, [defaultSubTab]);

  useEffect(() => {
    determineUserRole();
  }, [jobId]);

  const determineUserRole = async () => {
    try {
      setLoading(true);
      
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return;

      // Check if user is an awarded vendor for this job
      const { data: award } = await supabase
        .from('awards')
        .select(`
          winning_bid_id,
          bids!inner (
            vendor_user_id
          )
        `)
        .eq('job_id', jobId)
        .eq('bids.vendor_user_id', userData.user.id)
        .maybeSingle();

      if (award) {
        setUserRole('vendor');
      } else {
        setUserRole('client');
      }
    } catch (error) {
      console.error('Error determining user role:', error);
      setUserRole('client'); // Default to client
    } finally {
      setLoading(false);
    }
  };

  const handleUploadComplete = () => {
    setRefreshKey(prev => prev + 1);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading documents...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Job File Sharing</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={currentSubTab} onValueChange={(value) => setCurrentSubTab(value as 'files' | 'upload')} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="files">View Files</TabsTrigger>
              <TabsTrigger value="upload">Upload Files</TabsTrigger>
            </TabsList>

            <TabsContent value="files">
              <JobFileViewer 
                key={refreshKey}
                jobId={jobId} 
                userRole={userRole}
              />
            </TabsContent>

            <TabsContent value="upload">
              <JobFileUpload 
                jobId={jobId} 
                userRole={userRole}
                onUploadComplete={handleUploadComplete}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};